// Timezone utilities for the schedule / live banner.
// Baseline for all show times stored in the DB is BASE_TZ (US Eastern, DST-aware).
// Times are stored as "HH:MM" strings + day_of_week (0=Sun ... 6=Sat).

export const BASE_TZ = "America/New_York";

// Grouped zones for the selector. Labels are short display names, but
// values are IANA identifiers so DST is handled by the browser.
export const TIMEZONE_GROUPS = [
  {
    label: "Americas",
    zones: [
      { value: "America/New_York", label: "US Eastern (EST/EDT)" },
      { value: "America/Chicago", label: "US Central (CST/CDT)" },
      { value: "America/Denver", label: "US Mountain (MST/MDT)" },
      { value: "America/Phoenix", label: "US Arizona (MST)" },
      { value: "America/Los_Angeles", label: "US Pacific (PST/PDT)" },
      { value: "America/Anchorage", label: "US Alaska (AKST/AKDT)" },
      { value: "Pacific/Honolulu", label: "US Hawaii (HST)" },
      { value: "America/Toronto", label: "Canada Eastern" },
      { value: "America/Vancouver", label: "Canada Pacific" },
      { value: "America/Mexico_City", label: "Mexico City" },
      { value: "America/Sao_Paulo", label: "São Paulo" },
      { value: "America/Buenos_Aires", label: "Buenos Aires" },
    ],
  },
  {
    label: "Europe / Africa",
    zones: [
      { value: "Europe/London", label: "London (GMT/BST)" },
      { value: "Europe/Dublin", label: "Dublin" },
      { value: "Europe/Lisbon", label: "Lisbon" },
      { value: "Europe/Paris", label: "Paris (CET/CEST)" },
      { value: "Europe/Berlin", label: "Berlin" },
      { value: "Europe/Madrid", label: "Madrid" },
      { value: "Europe/Rome", label: "Rome" },
      { value: "Europe/Amsterdam", label: "Amsterdam" },
      { value: "Europe/Stockholm", label: "Stockholm" },
      { value: "Europe/Athens", label: "Athens (EET/EEST)" },
      { value: "Europe/Moscow", label: "Moscow (MSK)" },
      { value: "Europe/Istanbul", label: "Istanbul" },
      { value: "Africa/Cairo", label: "Cairo" },
      { value: "Africa/Johannesburg", label: "Johannesburg" },
      { value: "Africa/Lagos", label: "Lagos" },
    ],
  },
  {
    label: "Asia / Middle East",
    zones: [
      { value: "Asia/Dubai", label: "Dubai (GST)" },
      { value: "Asia/Jerusalem", label: "Jerusalem" },
      { value: "Asia/Riyadh", label: "Riyadh" },
      { value: "Asia/Karachi", label: "Karachi" },
      { value: "Asia/Kolkata", label: "India (IST)" },
      { value: "Asia/Dhaka", label: "Dhaka" },
      { value: "Asia/Bangkok", label: "Bangkok" },
      { value: "Asia/Jakarta", label: "Jakarta" },
      { value: "Asia/Singapore", label: "Singapore" },
      { value: "Asia/Hong_Kong", label: "Hong Kong" },
      { value: "Asia/Shanghai", label: "Shanghai (CST)" },
      { value: "Asia/Tokyo", label: "Tokyo (JST)" },
      { value: "Asia/Seoul", label: "Seoul (KST)" },
      { value: "Asia/Manila", label: "Manila" },
    ],
  },
  {
    label: "Oceania",
    zones: [
      { value: "Australia/Perth", label: "Perth (AWST)" },
      { value: "Australia/Adelaide", label: "Adelaide (ACST/ACDT)" },
      { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
      { value: "Australia/Brisbane", label: "Brisbane" },
      { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
      { value: "Pacific/Fiji", label: "Fiji" },
    ],
  },
  {
    label: "Universal",
    zones: [
      { value: "UTC", label: "UTC" },
    ],
  },
];

export const ALL_ZONES = TIMEZONE_GROUPS.flatMap((g) => g.zones);

export function zoneLabel(value) {
  const z = ALL_ZONES.find((x) => x.value === value);
  return z ? z.label : value;
}

// Return the offset in minutes of `tz` relative to UTC at the moment `date`.
// Positive if `tz` is ahead of UTC. Uses formatToParts so DST is respected.
export function tzOffsetMinutes(date, tz) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const m = {};
  parts.forEach((p) => { if (p.type !== "literal") m[p.type] = p.value; });
  const hour = parseInt(m.hour, 10) % 24;
  const asUTC = Date.UTC(
    parseInt(m.year, 10),
    parseInt(m.month, 10) - 1,
    parseInt(m.day, 10),
    hour,
    parseInt(m.minute, 10),
    parseInt(m.second, 10),
  );
  return Math.round((asUTC - date.getTime()) / 60000);
}

// Return a Date whose UTC instant matches the wall-clock time `y-mo-d h:mi` in `tz`.
function zonedDateToUTC(y, mo, d, h, mi, tz) {
  // First guess: interpret as UTC and correct by tz offset at that instant.
  const guess = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
  const off = tzOffsetMinutes(guess, tz);
  // If the wall-clock hour of `guess` when viewed in `tz` matches (h), we're done.
  // Otherwise correct by the offset once more (handles DST edges reliably).
  const corrected = new Date(guess.getTime() - off * 60000);
  return corrected;
}

// Reference Sunday: an anchor date used only to convert day-of-week + HH:MM
// in the base timezone to a display day-of-week + HH:MM in a target timezone.
// We use a fixed reference to keep results stable across the whole month view.
function referenceSundayForNow() {
  const now = new Date();
  const localSunday = new Date(now);
  localSunday.setDate(now.getDate() - now.getDay());
  localSunday.setHours(12, 0, 0, 0); // midday avoids DST edge on the exact reference day
  return localSunday;
}

// Convert a (day_of_week, "HH:MM") pair in BASE_TZ to the same instant expressed
// in `targetTz`. Returns { day: 0-6, time: "HH:MM" }.
export function convertBaseToTz(dayOfWeek, timeStr, targetTz) {
  if (!targetTz || targetTz === BASE_TZ) {
    return { day: dayOfWeek, time: timeStr };
  }
  const [h, m] = String(timeStr || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
  const ref = referenceSundayForNow();
  const y = ref.getFullYear();
  const mo = ref.getMonth() + 1;
  const d = ref.getDate() + dayOfWeek;
  // Instant that represents "d/mo/y h:m in BASE_TZ"
  const utc = zonedDateToUTC(y, mo, d, h, m, BASE_TZ);
  // Read that instant back in targetTz
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: targetTz,
    hour12: false,
    weekday: "short",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).formatToParts(utc);
  const p = {};
  parts.forEach((x) => { if (x.type !== "literal") p[x.type] = x.value; });
  // Compute delta days between the target-tz wall-clock day and our reference sunday date.
  const targetY = parseInt(p.year, 10);
  const targetMo = parseInt(p.month, 10);
  const targetD = parseInt(p.day, 10);
  const refMidnight = new Date(y, mo - 1, ref.getDate()); // ref sunday midnight local
  const targetMidnight = new Date(targetY, targetMo - 1, targetD);
  const deltaDays = Math.round((targetMidnight - refMidnight) / 86400000);
  const newDay = ((deltaDays % 7) + 7) % 7;
  const hh = parseInt(p.hour, 10) % 24;
  const mm = parseInt(p.minute, 10);
  const time = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  return { day: newDay, time };
}

// Convert a show's (day, start_time, end_time) triple. Duration is preserved;
// only the anchor day + start time move. End time is recomputed from duration.
export function convertShowTimes(dayOfWeek, startTime, endTime, targetTz) {
  const { day, time: newStart } = convertBaseToTz(dayOfWeek, startTime, targetTz);
  // Preserve original duration (wrapping midnight if needed).
  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  const s = toMin(startTime);
  const e = toMin(endTime);
  const dur = e >= s ? e - s : (24 * 60 - s) + e;
  const ns = toMin(newStart);
  const ne = (ns + dur) % (24 * 60);
  const newEnd = `${String(Math.floor(ne / 60)).padStart(2, "0")}:${String(ne % 60).padStart(2, "0")}`;
  return { day, start_time: newStart, end_time: newEnd };
}

// Return an unnormalized "hour offset" between BASE_TZ and targetTz at a given
// instant. Useful to shift the schedule grid's hour rows.
export function baseHourOffset(targetTz, at = new Date()) {
  if (!targetTz || targetTz === BASE_TZ) return 0;
  const base = tzOffsetMinutes(at, BASE_TZ);
  const tgt = tzOffsetMinutes(at, targetTz);
  return (tgt - base) / 60; // hours (may be fractional for zones like IST +05:30)
}

// Format the current "moment" as a wall-clock in a given timezone: { day, hh, mm }
export function nowInTz(tz) {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    weekday: "short",
    hour: "2-digit", minute: "2-digit",
  }).formatToParts(now);
  const p = {};
  parts.forEach((x) => { if (x.type !== "literal") p[x.type] = x.value; });
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    day: weekdayMap[p.weekday] ?? 0,
    hh: parseInt(p.hour, 10) % 24,
    mm: parseInt(p.minute, 10) || 0,
  };
}

// Return the browser's IANA timezone if it's one we support, else BASE_TZ.
export function detectDefaultTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (ALL_ZONES.some((z) => z.value === tz)) return tz;
    // Best-effort mapping: pick the closest option by offset if the browser
    // reports a zone we don't list explicitly (e.g. Europe/Bucharest -> Athens).
    const now = new Date();
    const browserOff = tzOffsetMinutes(now, tz);
    let best = BASE_TZ;
    let bestDiff = Infinity;
    for (const z of ALL_ZONES) {
      const diff = Math.abs(tzOffsetMinutes(now, z.value) - browserOff);
      if (diff < bestDiff) { bestDiff = diff; best = z.value; }
    }
    return best;
  } catch {
    return BASE_TZ;
  }
}
